import { useQuery } from '@tanstack/react-query'
import { Alert, Badge, Flex, Segmented, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { supportTicketsListOptions, type SupportTicket } from 'src/api/main'
import { DateTimeCell, ProTableContainer } from 'src/components'
import {
  defaultPageSizeOptions,
  getPaginationVisibilityConfig
} from 'src/hooks'

import { PriorityLabel } from '../PriorityLabel'
import { StatusBadge } from '../StatusBadge'
import { TicketConversation } from '../TicketConversation'

import { useStyles } from './styles'

type TicketStatusFilter = 'open' | 'completed'

export const SubmittedTicketsList = () => {
  const { styles, cx } = useStyles()
  const { t } = useTranslation()
  const [statusFilter, setStatusFilter] = useState<TicketStatusFilter>('open')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(() =>
    Number(defaultPageSizeOptions[0])
  )
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(
    null
  )

  const ticketsQuery = useQuery({
    ...supportTicketsListOptions({
      query: { status: statusFilter, page, per_page: pageSize }
    })
  })

  const total = ticketsQuery.data?.count ?? 0

  const onStatusFilterChange = (value: TicketStatusFilter) => {
    setStatusFilter(value)
    setPage(1)
  }

  const columns: ColumnsType<SupportTicket> = [
    {
      title: t('tickets.ticket'),
      dataIndex: 'title',
      key: 'title',
      fixed: 'left',
      width: 220,
      onCell: () => ({ className: styles.badgeCell }),
      render: (_: unknown, record: SupportTicket) => {
        const hasUpdate = !record.completed && record.has_unseen_update
        const title = (
          <Typography.Link
            ellipsis
            className={cx(styles.title, hasUpdate && styles.titleUpdated)}
            title={record.title || '—'}
          >
            {record.title || '—'}
          </Typography.Link>
        )

        if (!hasUpdate) return title

        return (
          <Badge status="error" text={title} title={t('tickets.hasUpdate')} />
        )
      }
    },
    {
      title: t('tickets.status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      onCell: () => ({ className: styles.badgeCell }),
      render: (_: unknown, record: SupportTicket) => (
        <StatusBadge ticket={record} />
      )
    },
    {
      title: t('tickets.priority.label'),
      dataIndex: 'priority',
      key: 'priority',
      width: 120,
      render: (_: unknown, record: SupportTicket) => (
        <PriorityLabel priority={record.priority} />
      )
    },
    {
      title: t('tickets.createdOn'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (_: unknown, record: SupportTicket) => (
        <DateTimeCell value={record.created_at} variant="date" />
      )
    },
    {
      title: t('tickets.startDate'),
      dataIndex: 'start_date',
      key: 'start_date',
      width: 100,
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (_: unknown, record: SupportTicket) => (
        <DateTimeCell value={record.start_date} variant="date" />
      )
    },
    {
      title: t('tickets.dueDate'),
      dataIndex: 'due_date',
      key: 'due_date',
      width: 100,
      onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
      render: (_: unknown, record: SupportTicket) => (
        <DateTimeCell value={record.due_date} variant="date" />
      )
    }
  ]

  if (ticketsQuery.isError) {
    return (
      <Alert
        title={t('tickets.listError')}
        description={ticketsQuery.error.message}
        type="error"
        showIcon
        action={
          <Typography.Link onClick={() => ticketsQuery.refetch()}>
            {t('tickets.retry')}
          </Typography.Link>
        }
      />
    )
  }

  return (
    <>
      <ProTableContainer<SupportTicket>
        tableIdentifier="organization-support-tickets"
        rowKey="id"
        data={ticketsQuery.data}
        loading={ticketsQuery.isLoading}
        showSearch={false}
        columns={columns}
        scroll={{ x: 600 }}
        locale={{ emptyText: t('tickets.noTickets') }}
        rowClassName={styles.row}
        onRow={(record) => ({
          onClick: () => setSelectedTicket(record),
          onKeyDown: (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return

            event.preventDefault()
            setSelectedTicket(record)
          },
          tabIndex: 0
        })}
        leftChildren={
          <Flex align="center" gap="small">
            <Typography.Text>{t('tickets.statusFilter')}:</Typography.Text>
            <Segmented<TicketStatusFilter>
              value={statusFilter}
              onChange={onStatusFilterChange}
              options={[
                { label: t('tickets.open'), value: 'open' },
                { label: t('tickets.closed'), value: 'completed' }
              ]}
            />
          </Flex>
        }
        pagination={{
          ...getPaginationVisibilityConfig(total, pageSize),
          current: page,
          pageSize,
          total,
          pageSizeOptions: defaultPageSizeOptions,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage)
            setPageSize(nextPageSize)
          }
        }}
      />

      {selectedTicket ? (
        <TicketConversation
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
        />
      ) : null}
    </>
  )
}
